/**
 * Per-role page switches for the staff training pages — TRUS-562.
 *
 * Each Learning/Training sidebar entry and staff-training tab is registered as a page
 * capability (backend migrations 202610161300 and 202610281200), so an admin can turn
 * it off for a role in Settings → Access Control → Permission Matrix.
 *
 * A switch can only NARROW what a role sees. It hides something only when the access
 * check positively says this role is switched off. These leave things as they were:
 *   - 'page_not_found': the capability isn't registered yet, or the check failed;
 *   - 'parent_capability_disabled': the org doesn't run the staff training program
 *     (LCC), so the switches don't apply to it.
 */
import { usePageCapability } from '@/hooks/usePageCapability';

const NOT_A_DECISION = new Set(['page_not_found', 'parent_capability_disabled']);

export function usePageSwitch(
  orgId: number | null,
  pageKey: string,
  roleType?: string,
): { loading: boolean; switchedOff: boolean } {
  const access = usePageCapability(orgId, pageKey, roleType as any);
  const switchedOff = !access.loading && !access.hasAccess && !NOT_A_DECISION.has(String(access.accessSource));
  return { loading: access.loading, switchedOff };
}

export const PAGE_SWITCH = {
  learningHub: 'workforce.learning_hub',
  myTrainings: 'workforce.learning_my_trainings',
  myTeam: 'workforce.learning_my_team',
  trainingHub: 'workforce.training_hub',
  catalogue: 'workforce.training_catalogue',
  compliance: 'workforce.training_compliance',
  reports: 'workforce.training_reports',
  records: 'workforce.training_records',
} as const;
